// 공유 카드 → 이미지 추출 + 저장/공유 (§8).
// 웹: <a download> / Web Share. 네이티브: 갤러리 저장(Media) / 파일 공유(Filesystem+Share).
import { toPng } from "html-to-image";
import { isNative, nativeSaveImage, nativeShareImage, nativeSaveVideo, nativeShareVideo } from "./native";
import type { RenderedVideo } from "./renderClient";

export async function captureCard(el: HTMLElement): Promise<string> {
  return toPng(el, { pixelRatio: 3, cacheBust: true, backgroundColor: "#000" });
}

function dataUrlToBlob(dataUrl: string): Blob {
  const [head, body] = dataUrl.split(",");
  const mime = head.match(/:(.*?);/)?.[1] ?? "image/png";
  const bin = atob(body);
  const arr = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
  return new Blob([arr], { type: mime });
}

export function downloadCard(dataUrl: string, name = "bwek.png"): void {
  const a = document.createElement("a");
  a.href = dataUrl;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** 저장: 네이티브 → 기기 갤러리. 웹 → 다운로드. 성공 여부 반환. */
export async function saveCard(dataUrl: string): Promise<boolean> {
  if (isNative()) return nativeSaveImage(dataUrl);
  downloadCard(dataUrl);
  return true;
}

/** 공유: 네이티브 → 이미지 파일 공유(전용). 웹 → Web Share(파일) → 다운로드 폴백. */
export async function shareCard(dataUrl: string, text = "너는 몇 점? bwek.app"): Promise<void> {
  // 네이티브는 무조건 네이티브 이미지 공유. 실패해도 WebView 텍스트 공유로 폴백하지 않는다
  // (그 폴백이 "텍스트+링크만" 공유되던 원인 — 카드 이미지가 빠짐).
  if (isNative()) {
    await nativeShareImage(dataUrl, text);
    return;
  }
  try {
    const blob = dataUrlToBlob(dataUrl);
    const file = new File([blob], "bwek.png", { type: "image/png" });
    const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
    if (nav.canShare && nav.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text });
      return;
    }
  } catch {
    /* fall through */
  }
  downloadCard(dataUrl);
}

// ── 영상(mp4) 저장/공유 ─────────────────────────────────────────
/** 저장: 네이티브 → 갤러리. 웹 → 다운로드. 성공 여부 반환. */
export async function saveVideo(v: RenderedVideo): Promise<boolean> {
  if (isNative()) return nativeSaveVideo(v.base64);
  const url = URL.createObjectURL(v.blob);
  downloadCard(url, "bwek.mp4");
  URL.revokeObjectURL(url);
  return true;
}

/** 공유: 네이티브 → 영상 파일 공유. 웹 → Web Share(파일) → 다운로드 폴백. */
export async function shareVideo(v: RenderedVideo, text: string): Promise<void> {
  if (isNative()) {
    await nativeShareVideo(v.base64, text);
    return;
  }
  try {
    const file = new File([v.blob], "bwek.mp4", { type: "video/mp4" });
    const nav = navigator as Navigator & { canShare?: (d: ShareData) => boolean };
    if (nav.canShare && nav.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text });
      return;
    }
  } catch {
    /* fall through */
  }
  const url = URL.createObjectURL(v.blob);
  downloadCard(url, "bwek.mp4");
  URL.revokeObjectURL(url);
}
